import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore, AngularFirestoreDocument } from '@angular/fire/compat/firestore';
import { Observable, Subject, BehaviorSubject, interval, Subscription } from 'rxjs';
import { switchMap, takeUntil, filter, map } from 'rxjs/operators';
import firebase from 'firebase/compat/app';

export interface UserPresence {
  online: boolean;
  lastActive: firebase.firestore.Timestamp;
  uid: string;
}

@Injectable({
  providedIn: 'root'
})
export class PresenceService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private heartbeatInterval?: Subscription;
  private currentUser: any = null;
  private readonly HEARTBEAT_INTERVAL = 30000; // 30 seconds
  private readonly ONLINE_THRESHOLD = 2 * 60 * 1000; // 2 minutes in milliseconds

  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore
  ) {
    // Only setup event handlers, don't auto-initialize
    this.setupBeforeUnloadHandler();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopHeartbeat();
  }

  /**
   * Initialize presence tracking for a specific user
   * Call this from your layout component when user data is loaded
   */
  public initializePresenceTracking(user: any): void {
    if (!user) {
      console.error('❌ Cannot initialize presence tracking: User is undefined');
      return;
    }
    
    // Check for uid or id property
    if (!user.uid && !user.id) {
      console.error('❌ Cannot initialize presence tracking: User has no UID or ID', user);
      return;
    }
    
    // Ensure user has a uid property (may be stored as id in some cases)
    if (!user.uid && user.id) {
      user.uid = user.id;
    }
    
    // Stop any existing tracking first
    this.stopPresenceTracking();
    
    // Set current user and start tracking
    this.currentUser = user;
    this.setUserOnline();
    this.startHeartbeat();
  }

  /**
   * Stop presence tracking
   */
  public stopPresenceTracking(): void {
    this.setUserOffline();
    this.stopHeartbeat();
    this.currentUser = null;
  }

  /**
   * Start the heartbeat mechanism to update user presence every 30 seconds
   */
  private startHeartbeat(): void {
    this.stopHeartbeat(); // Ensure no duplicate intervals
    
    this.heartbeatInterval = interval(this.HEARTBEAT_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.currentUser) {
          this.updateUserPresence(true);
        }
      });
  }

  /**
   * Stop the heartbeat mechanism
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      this.heartbeatInterval.unsubscribe();
      this.heartbeatInterval = undefined;
    }
  }

  /**
   * Set user as online when they log in or start the app
   */
  public setUserOnline(): void {
    if (this.currentUser) {
      this.updateUserPresence(true);
    }
  }

  /**
   * Set user as offline when they log out or close the app
   */
  public setUserOffline(): void {
    if (this.currentUser) {
      this.updateUserPresence(false);
    }
  }

  /**
   * Update user presence in Firestore
   */
  private updateUserPresence(online: boolean): void {
    if (!this.currentUser) return;

    const userId = this.currentUser.uid || this.currentUser.id;
    const userRef: AngularFirestoreDocument<any> = this.afs.doc(`users/${userId}`);
    
    const presenceData = {
      online: online,
      lastActive: firebase.firestore.FieldValue.serverTimestamp()
    };

    userRef.update(presenceData).catch(error => {
      console.error('❌ Error updating user presence:', error);
    });
  }

  /**
   * Setup beforeunload event handler to mark user as offline when they close/refresh the app
   */
  private setupBeforeUnloadHandler(): void {
    window.addEventListener('beforeunload', () => {
      if (this.currentUser) {
        // Use navigator.sendBeacon for reliable offline status update during page unload
        const presenceData = {
          online: false,
          lastActive: new Date().toISOString()
        };
        
        // Attempt synchronous update first (may work in some browsers)
        try {
          // Get the projectId from Firebase config
          const firebaseConfig = this.afs.firestore.app.options as {[key: string]: any};
          const projectId = firebaseConfig['projectId'] as string;
          navigator.sendBeacon(
            `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/users/${this.currentUser.uid}`,
            JSON.stringify({
              fields: {
                online: { booleanValue: false },
                lastActive: { timestampValue: new Date().toISOString() }
              }
            })
          );
        } catch (error) {
          // Fallback to regular update if sendBeacon fails
          this.updateUserPresence(false);
        }
      }
    });

    // Also handle visibility change (when user switches tabs/minimizes)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // console.log('👁️ Page hidden - setting user offline');
        this.setUserOffline();
      } else if (document.visibilityState === 'visible' && this.currentUser) {
        // console.log('👁️ Page visible - setting user online');
        this.setUserOnline();
        this.startHeartbeat(); // Restart heartbeat when user returns
      }
    });
  }

  /**
   * Get user presence status by uid
   */
  public getUserPresence(uid: string): Observable<UserPresence | null> {
    return this.afs.doc<UserPresence>(`users/${uid}`).valueChanges().pipe(
      map((presence: UserPresence | undefined) => presence || null)
    );
  }

  /**
   * Check if a user is currently online based on their lastActive timestamp
   */
  public isUserOnline(lastActive: firebase.firestore.Timestamp | null, onlineFlag?: boolean): boolean {
    if (!lastActive) return false;
    
    // If the user explicitly marked themselves as offline, respect that
    if (onlineFlag === false) return false;
    
    const now = new Date();
    const lastActiveDate = lastActive.toDate();
    const timeDifference = now.getTime() - lastActiveDate.getTime();
    
    return timeDifference <= this.ONLINE_THRESHOLD;
  }

  /**
   * Get formatted "last seen" text for a user
   */
  public getLastSeenText(lastActive: firebase.firestore.Timestamp | null, onlineFlag?: boolean): string {
    if (!lastActive) return 'Never seen';
    
    if (this.isUserOnline(lastActive, onlineFlag)) {
      return '✅ Online';
    }
    
    const lastActiveDate = lastActive.toDate();
    const now = new Date();
    const timeDifference = now.getTime() - lastActiveDate.getTime();
    
    // Format relative time
    const minutes = Math.floor(timeDifference / (1000 * 60));
    const hours = Math.floor(timeDifference / (1000 * 60 * 60));
    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    
    if (minutes < 60) {
      return `⏱️ Last seen ${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (hours < 24) {
      return `⏱️ Last seen ${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      return `⏱️ Last seen ${days} day${days !== 1 ? 's' : ''} ago`;
    }
  }

  /**
   * Handle logout - set user offline and stop heartbeat
   */
  public handleLogout(): void {
    this.setUserOffline();
    this.stopHeartbeat();
  }

  /**
   * Get multiple users' presence status
   */
  public getMultipleUsersPresence(uids: string[]): Observable<UserPresence[]> {
    if (uids.length === 0) {
      return new BehaviorSubject<UserPresence[]>([]).asObservable();
    }

    return this.afs.collection<UserPresence>('users', ref => 
      ref.where('uid', 'in', uids)
    ).valueChanges();
  }

  /**
   * Get current user's presence status
   */
  public getCurrentUserPresence(): Observable<UserPresence | null> {
    if (!this.currentUser) {
      return new BehaviorSubject<UserPresence | null>(null).asObservable();
    }
    return this.getUserPresence(this.currentUser.uid);
  }
}